/**
 * Kapsule výberu variant zákazky (KOLO 206, spoločné od KOLA 207) —
 * Objednávky (z ktorých variant je súpis) a okno Poslať do Cut Rite
 * (ktoré časti idú do zoznamu). Varianty prichádzajú v poradí záložiek;
 * posledná zaškrtnutá sa odškrtnúť nedá — prázdny výber nedáva zmysel.
 */
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface KapsulaVarianty {
  id: string;
  nazov: string;
  /** číslo záložky — tá istá predpona ako v kódoch (`2-A1`) */
  cislo: number | string;
  vybrana: boolean;
  /** sivý dovetok, napr. „výrobná" */
  druh?: string | null;
}

export function KapsuleVariant({
  varianty,
  onZmena,
  ariaLabel,
  predvolene,
  className,
}: {
  varianty: readonly KapsulaVarianty[];
  /** id zaškrtnutých v poradí záložiek */
  onZmena: (ids: string[]) => void;
  ariaLabel: string;
  /** Tlačidlo návratu na predvolený výber — len keď je výber vlastný. */
  predvolene?: { title: string; onClick: () => void } | null;
  className?: string;
}): JSX.Element {
  function prepni(id: string): void {
    const vybrane = new Set(varianty.filter((v) => v.vybrana).map((v) => v.id));
    if (vybrane.has(id)) vybrane.delete(id);
    else vybrane.add(id);
    if (vybrane.size === 0) return;
    onZmena(varianty.filter((v) => vybrane.has(v.id)).map((v) => v.id));
  }

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2 text-sm", className)}
      role="group"
      aria-label={ariaLabel}
    >
      <span className="text-muted-foreground">Varianty:</span>
      {varianty.map((v) => (
        <label
          key={v.id}
          className={cn(
            "inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
            v.vybrana ? "border-primary bg-primary/10" : "text-muted-foreground",
          )}
        >
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-primary"
            checked={v.vybrana}
            onChange={() => prepni(v.id)}
            aria-label={v.nazov}
          />
          <span className="font-mono">{v.cislo}</span>
          {v.nazov}
          {v.druh ? <span className="text-muted-foreground">· {v.druh}</span> : null}
        </label>
      ))}
      {predvolene ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          title={predvolene.title}
          onClick={predvolene.onClick}
        >
          Predvolené
        </Button>
      ) : null}
    </div>
  );
}
